import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import ChakraWrapper from "./wrappers/ChakraWrapper";
import { AuthProvider } from "./wrappers/UseAuthentication";
import { SupplierRetailerProvider } from "./wrappers/SupplierRetailerContext";
import { PageAccessProvider } from "./wrappers/PageAccessContext";

import AuthenticatedLayout from "./components/layout/AuthenticatedLayout";
import RequireAuth from "./components/RequireAuth";

import Landing from "./pages/landing/Landing";
import Signin from "./pages/login/Signin";
import Register from "./pages/login/Register";
import Contact from "./pages/marketing/Contact";
import About from "./pages/marketing/About";
import TermsConditions from "./pages/legal/TermsConditions";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";

import Sales from "./pages/sales/Sales";
import StrikeRate from "./pages/strike/StrikeRate";
import Ranging from "./pages/ranging/Ranging";
import Margin from "./pages/margin/Margin";
import Insights from "./pages/insights/Insights";
import Orders from "./pages/orders/Orders";
import MyProfile from "./pages/myProfile/MyProfile";

import Users from "./pages/admin/users/Users";
import AddUser from "./pages/admin/users/AddUser";
import EditPageAccess from "./pages/admin/users/EditPageAccess";
import EditSupplierAccess from "./pages/admin/users/EditSupplierAccess";
import EditRetailerAccess from "./pages/admin/users/EditRetailerAccess";
import SupplierLogos from "./pages/admin/users/SupplierLogos";
import Organisations from "./pages/admin/users/Organisations";
import OrgUsers from "./pages/admin/users/OrgUsers";
import OrgReports from "./pages/admin/reports/OrgReports";
import Articles from "./pages/admin/articles/Articles";
import Stores from "./pages/admin/stores/Stores";
import LoginHistory from "./pages/admin/history/LoginHistory";
import Imports from "./pages/admin/imports/Imports";
import Edi from "./pages/admin/edi/Edi";
import Alerts from "./pages/admin/alerts/Alerts";
import PnpFtp from "./pages/admin/pnpFtp/PnpFtp";
import Delete from "./pages/admin/delete/Delete";
import Validation from "./pages/admin/validation/Validation";
import BatchReports from "./pages/admin/batchReports/BatchReports";
import DataStatus from "./pages/admin/dataStatus/DataStatus";

// The /users route serves two different screens: masters/admins get the full
// Users admin, org admins get their scoped, password-free OrgUsers screen.
const UsersByRole = () => (localStorage.getItem("user_type") === "org_admin" ? <OrgUsers /> : <Users />);

const withHelmet = (title, description, Component) => (
  <>
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
    </Helmet>
    <Component />
  </>
);

const AppRoutes = () => {
  // Session restore lives in AuthProvider — see wrappers/UseAuthentication.
  return (
    <Routes>
      {/* Marketing landing — standalone, no PublicLayout chrome */}
      <Route path="/" element={<Landing />} />

      {/* Sign-in — standalone dark-glass page, brings its own LandingNavbar/Footer */}
      <Route path="/signin" element={<Signin />} />
      <Route path="/register" element={<Register />} />

      {/* Marketing + legal — standalone pages, each brings its own LandingNavbar + MarketingFooter */}
      <Route path="/about-us" element={<About />} />
      <Route path="/contact-us" element={<Contact />} />
      <Route
        path="/terms"
        element={withHelmet(
          "Terms & Conditions - SpearBI",
          "Terms and conditions for using the SpearBI Reporting System",
          TermsConditions,
        )}
      />
      <Route
        path="/privacy"
        element={withHelmet(
          "Privacy Policy - SpearBI",
          "Privacy policy for the SpearBI Reporting System",
          PrivacyPolicy,
        )}
      />

      {/* Authenticated — sidebar shell */}
      <Route element={<RequireAuth />}>
        <Route element={<AuthenticatedLayout />}>
          <Route
            path="/myProfile"
            element={withHelmet("MyProfile - Spearbi", "Edit & manage your profile & subscription", MyProfile)}
          />
          <Route
            path="/sales"
            element={withHelmet(
              "Sales - Spearbi",
              "Analyze your sales data comprehensively with Spearbi's reporting tools.",
              Sales,
            )}
          />
          <Route
            path="/strike"
            element={withHelmet(
              "Strike Rate - Spearbi",
              "Evaluate and improve your strike rate with Spearbi's advanced analytics.",
              StrikeRate,
            )}
          />
          <Route
            path="/ranging"
            element={withHelmet(
              "Ranging - Spearbi",
              "Identify active articles in stores across selected retailers with Spearbi's reporting tools.",
              Ranging,
            )}
          />
          <Route
            path="/margin"
            element={withHelmet(
              "Margin Report - Spearbi",
              "Analyze your margin report comprehensively with Spearbi's reporting tools.",
              Margin,
            )}
          />
          <Route
            path="/insights"
            element={withHelmet(
              "Insights - Spearbi",
              "Advanced insights and analytics for Spearbi administrators.",
              Insights,
            )}
          />
          <Route
            path="/orders"
            element={withHelmet("Orders - Spearbi", "PNP Orders management for Spearbi administrators.", Orders)}
          />
          <Route
            path="/imports"
            element={withHelmet(
              "Imports - Spearbi",
              "Manage and analyze your imports with Spearbi's robust reporting features.",
              Imports,
            )}
          />
          <Route
            path="/users"
            element={withHelmet(
              "Users - Spearbi",
              "Manage user accounts and permissions within the Spearbi system.",
              UsersByRole,
            )}
          />
          <Route
            path="/articles"
            element={withHelmet(
              "Articles - Spearbi",
              "Manage supplier articles and permissions within the Spearbi system.",
              Articles,
            )}
          />
          <Route
            path="/stores"
            element={withHelmet(
              "Stores - Spearbi",
              "Manage retailer stores and permissions within the Spearbi system.",
              Stores,
            )}
          />
          <Route
            path="/loginhistory"
            element={withHelmet(
              "Login History - Spearbi",
              "Admin functionality to view login history within the Spearbi system.",
              LoginHistory,
            )}
          />
          <Route
            path="/edi"
            element={withHelmet("EDI - Spearbi", "Manage Edi functionality for all Spear bi's selected retailers", Edi)}
          />
          <Route
            path="/alerts"
            element={withHelmet("Alerts - Spearbi", "Run the Site Days Cover stock-alert jobs on demand", Alerts)}
          />
          <Route
            path="/delete"
            element={withHelmet("EDI - Spearbi", "Delete & manage data within Spearbi's system", Delete)}
          />
          <Route
            path="/pnp-ftp"
            element={withHelmet("PNP FTP - Spearbi", "Monitor PNP EDI file processing and FTP status", PnpFtp)}
          />
          <Route
            path="/validation"
            element={withHelmet("Validation - Spearbi", "Data validation tools", Validation)}
          />
          <Route
            path="/data-status"
            element={withHelmet("Data Status - Spearbi", "Daily per-supplier import status per retailer", DataStatus)}
          />
          <Route
            path="/batch-reports"
            element={withHelmet(
              "Batch Reports - Spearbi",
              "Generate and email batch sales tracker reports",
              BatchReports,
            )}
          />

          <Route path="/addUser" element={<AddUser />} />
          <Route path="/editPageAccess/:userid" element={<EditPageAccess />} />
          <Route path="/editSupplierAccess/:userid" element={<EditSupplierAccess />} />
          <Route path="/editRetailerAccess/:userid" element={<EditRetailerAccess />} />
          <Route path="/supplierLogos" element={<SupplierLogos />} />
          <Route path="/organisations" element={<Organisations />} />
          <Route path="/org-reports" element={<OrgReports />} />

          {/* Authed catch-all → /sales */}
          <Route path="*" element={<Navigate to="/sales" replace />} />
        </Route>
      </Route>

      {/* Public catch-all → landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ChakraWrapper>
      <SupplierRetailerProvider>
        <PageAccessProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </PageAccessProvider>
      </SupplierRetailerProvider>
    </ChakraWrapper>
  );
}

export default App;
